export type BalloonRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export type BalloonDock = "auto" | "right" | "below";

export type Dock = "above" | "below" | "left" | "right" | "inside";
export type Edge = "start" | "end";

export type BalloonPlace = { dock: Dock; x: Edge; y: Edge };

export type PixelBox = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export type PlacedBalloon = {
  place: BalloonPlace;
  box: PixelBox;
};

export type PlaceableIssue = {
  id: string;
  rect: BalloonRect;
  dock?: BalloonDock;
};

const BALLOON_HEIGHT_PX = 100;
const BALLOON_WIDTH_PX = 232;
const BALLOON_WIDTH_NARROW_PX = 180;
const CAPTION_HEIGHT_PX = 40;
const EDGE_PAD_PX = 8;
const BALLOON_GAP_PX = 8;
const SLIDE_STEP_PX = 16;
const AUTO_DOCKS: Dock[] = ["above", "below", "left", "right", "inside"];

function balloonWidth(frameWidth: number) {
  return frameWidth < 640 ? BALLOON_WIDTH_NARROW_PX : BALLOON_WIDTH_PX;
}

export function preferredBalloonPlace(
  rect: BalloonRect,
  frameSize: { width: number; height: number },
  dockMode: BalloonDock,
): BalloonPlace {
  if (frameSize.width <= 0 || frameSize.height <= 0) {
    return {
      dock: rect.top + rect.height > 52 ? "above" : "below",
      x: "start",
      y: "start",
    };
  }

  const size = balloonSize(frameSize);
  const finding = findingPixels(rect, frameSize);
  const spaceAbove = finding.top;
  const spaceBelow = frameSize.height - finding.bottom - CAPTION_HEIGHT_PX;
  const spaceLeft = finding.left;
  const spaceRight = frameSize.width - finding.right;
  const needH = size.height + EDGE_PAD_PX;
  const needW = size.width + EDGE_PAD_PX;
  const x: Edge = finding.left + size.width > frameSize.width - EDGE_PAD_PX ? "end" : "start";
  const y: Edge = finding.top + size.height > frameSize.height - CAPTION_HEIGHT_PX - EDGE_PAD_PX ? "end" : "start";

  if (dockMode === "right") {
    return {
      dock: "right",
      x: "start",
      y,
    };
  }

  if (dockMode === "below") {
    return { dock: "below", x, y: "start" };
  }

  let dock: Dock;
  if (spaceAbove >= needH) {
    dock = "above";
  } else if (spaceBelow >= needH) {
    dock = "below";
  } else if (spaceLeft >= needW) {
    dock = "left";
  } else if (spaceRight >= needW) {
    dock = "right";
  } else {
    dock = "inside";
  }

  return { dock, x, y };
}

export function placeIssueBalloons(
  issues: PlaceableIssue[],
  frameSize: { width: number; height: number },
  defaultDock: BalloonDock = "auto",
): Map<string, PlacedBalloon> {
  const placed = new Map<string, PlacedBalloon>();
  const occupied: PixelBox[] = [];

  for (const issue of issues) {
    const dockMode = issue.dock ?? defaultDock;
    const placement = placeOneBalloon(issue.rect, frameSize, dockMode, occupied);
    placed.set(issue.id, placement);
    if (placement.box.width > 0 && placement.box.height > 0) {
      occupied.push(placement.box);
    }
  }

  return placed;
}

function placeOneBalloon(
  rect: BalloonRect,
  frameSize: { width: number; height: number },
  dockMode: BalloonDock,
  occupied: PixelBox[],
): PlacedBalloon {
  const preferred = preferredBalloonPlace(rect, frameSize, dockMode);
  if (frameSize.width <= 0 || frameSize.height <= 0) {
    return { place: preferred, box: { left: 0, top: 0, width: 0, height: 0 } };
  }

  const candidates = candidatePlaces(preferred, dockMode);
  let best: PlacedBalloon | null = null;
  let bestOverlap = Number.POSITIVE_INFINITY;

  for (const place of candidates) {
    const box = clampBox(balloonBox(rect, frameSize, place), frameSize, place.dock);
    const overlap = totalOverlap(box, occupied);
    if (overlap === 0 && !boxesTooClose(box, occupied)) {
      return { place, box };
    }

    if (overlap < bestOverlap) {
      bestOverlap = overlap;
      best = { place, box };
    }
  }

  for (const place of candidates) {
    const box = clampBox(balloonBox(rect, frameSize, place), frameSize, place.dock);
    const slid = slideBox(box, frameSize, place.dock, occupied);
    if (slid) {
      return { place, box: slid };
    }
  }

  return (
    best ?? {
      place: preferred,
      box: clampBox(balloonBox(rect, frameSize, preferred), frameSize, preferred.dock),
    }
  );
}

function candidatePlaces(preferred: BalloonPlace, dockMode: BalloonDock): BalloonPlace[] {
  const first: Dock = dockMode === "right" ? "right" : dockMode === "below" ? "below" : preferred.dock;
  const docks = [first, preferred.dock, ...AUTO_DOCKS].filter((dock, index, list) => list.indexOf(dock) === index);
  const seen = new Set<string>();
  const places: BalloonPlace[] = [];

  const add = (place: BalloonPlace) => {
    const key = `${place.dock}:${place.x}:${place.y}`;
    if (seen.has(key)) {
      return;
    }

    seen.add(key);
    places.push(place);
  };

  add(preferred);
  for (const dock of docks) {
    add({ dock, x: preferred.x, y: preferred.y });
    add({ dock, x: flipEdge(preferred.x), y: preferred.y });
    add({ dock, x: preferred.x, y: flipEdge(preferred.y) });
    add({ dock, x: flipEdge(preferred.x), y: flipEdge(preferred.y) });
  }

  return places;
}

function balloonBox(rect: BalloonRect, frameSize: { width: number; height: number }, place: BalloonPlace): PixelBox {
  const size = balloonSize(frameSize);
  const finding = findingPixels(rect, frameSize);
  const startLeft = place.x === "end" ? finding.right - size.width : finding.left;
  const startTop = place.y === "end" ? finding.bottom - size.height : finding.top;
  const midTop = (finding.top + finding.bottom) / 2 - size.height / 2;

  if (place.dock === "above") {
    return { left: startLeft, top: finding.top - size.height, width: size.width, height: size.height };
  }

  if (place.dock === "below") {
    return { left: startLeft, top: finding.bottom, width: size.width, height: size.height };
  }

  if (place.dock === "left") {
    return { left: finding.left - size.width, top: midTop, width: size.width, height: size.height };
  }

  if (place.dock === "right") {
    return { left: finding.right, top: midTop, width: size.width, height: size.height };
  }

  return { left: startLeft, top: startTop, width: size.width, height: size.height };
}

function clampBox(box: PixelBox, frameSize: { width: number; height: number }, dock?: Dock): PixelBox {
  const maxLeft = Math.max(EDGE_PAD_PX, frameSize.width - box.width - EDGE_PAD_PX);
  const maxTop = Math.max(EDGE_PAD_PX, frameSize.height - CAPTION_HEIGHT_PX - box.height - EDGE_PAD_PX);

  return {
    ...box,
    left: Math.min(Math.max(EDGE_PAD_PX, box.left), maxLeft),
    top: dock === "below" ? box.top : Math.min(Math.max(EDGE_PAD_PX, box.top), maxTop),
  };
}

function slideBox(
  box: PixelBox,
  frameSize: { width: number; height: number },
  dock: Dock,
  occupied: PixelBox[],
): PixelBox | null {
  const alongX = dock === "above" || dock === "below" || dock === "inside";
  const maxShift = alongX ? frameSize.width : frameSize.height;

  for (const direction of [1, -1]) {
    for (let shift = SLIDE_STEP_PX; shift <= maxShift; shift += SLIDE_STEP_PX) {
      const trial = clampBox(
        {
          ...box,
          left: alongX ? box.left + direction * shift : box.left,
          top: alongX ? box.top : box.top + direction * shift,
        },
        frameSize,
        dock,
      );

      if (trial.left === box.left && trial.top === box.top) {
        continue;
      }

      if (totalOverlap(trial, occupied) === 0 && !boxesTooClose(trial, occupied)) {
        return trial;
      }
    }
  }

  return null;
}

function balloonSize(frameSize: { width: number; height: number }) {
  return { width: balloonWidth(frameSize.width), height: BALLOON_HEIGHT_PX };
}

function findingPixels(rect: BalloonRect, frameSize: { width: number; height: number }) {
  return {
    left: (rect.left / 100) * frameSize.width,
    top: (rect.top / 100) * frameSize.height,
    right: ((rect.left + rect.width) / 100) * frameSize.width,
    bottom: ((rect.top + rect.height) / 100) * frameSize.height,
  };
}

function flipEdge(edge: Edge): Edge {
  return edge === "start" ? "end" : "start";
}

function boxesTooClose(box: PixelBox, occupied: PixelBox[]) {
  return occupied.some((other) => boxesOverlap(inflateBox(box, BALLOON_GAP_PX), other));
}

function totalOverlap(box: PixelBox, occupied: PixelBox[]) {
  return occupied.reduce((sum, other) => sum + overlapArea(box, other), 0);
}

export function overlapArea(first: PixelBox, second: PixelBox) {
  const width = Math.max(
    0,
    Math.min(first.left + first.width, second.left + second.width) - Math.max(first.left, second.left),
  );
  const height = Math.max(
    0,
    Math.min(first.top + first.height, second.top + second.height) - Math.max(first.top, second.top),
  );

  return width * height;
}

function boxesOverlap(first: PixelBox, second: PixelBox) {
  return overlapArea(first, second) > 0;
}

function inflateBox(box: PixelBox, pad: number): PixelBox {
  return {
    left: box.left - pad,
    top: box.top - pad,
    width: box.width + pad * 2,
    height: box.height + pad * 2,
  };
}
